import React, { useState } from 'react';
import { Card, Form, Input, Select, Button, message, Typography, Space, Spin, Divider } from 'antd';
import { ApiService } from '@/services/api';
import { handleError } from '@/utils/ui-helpers';
import { TemplateType } from '@/constants/templateTypes';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

interface DebugFormData {
  Content: string;
  Industry: string;
  Output_type: string;
  User_Define_Template?: string;
  workflow_id: string;
}

const DebugPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [outputType, setOutputType] = useState<string>(TemplateType.DEEP);

  const handleSubmit = async (values: DebugFormData) => {
    setLoading(true);
    setResult(null);
    
    try {
      const requestData: any = {
        Content: values.Content,
        Industry: values.Industry,
        Output_type: values.Output_type,
        app_id: "1",
        audioNum: 0
      };
      
      requestData.User_Define_Template = "";
      // 如果选择的是自定义模板，则添加User_Define_Template字段
      if (values.Output_type === TemplateType.CUSTOM && values.User_Define_Template) {
        requestData.User_Define_Template = values.User_Define_Template;
      }
      
      const response = await ApiService.chatDebug({
        app_id: '', 
        workflow_id: values.workflow_id || '', // 为空时传递空字符串
        parameters: requestData
      });
      
      setResult(response.data);
      message.success('调试请求成功');
    } catch (error) {
      handleError(error, '调试请求失败', 'debug', undefined, '');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    setOutputType(TemplateType.DEEP);
  };

  const formatJsonDisplay = (data: any) => {
    if (!data) return '';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <Title level={3} className="mb-2">
          工作流调试
        </Title>
        <Text type="secondary">
          测试和调试工作流参数，查看响应结果
        </Text>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 输入表单 */}
        <Card 
          title="输入参数" 
          className="h-fit"
          extra={
            <Space>
              <Button onClick={handleReset} disabled={loading}>
                重置
              </Button>
              <Button 
                type="primary" 
                onClick={() => form.submit()}
                loading={loading}
              >
                发送请求
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              Industry: '企业服务',
              Output_type: TemplateType.DEEP
            }}
          >
            <Form.Item
              label="工作流ID"
              name="workflow_id"
            >
              <Input
                placeholder="请输入工作流ID（为空则使用服务端id）"
                maxLength={100}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label="内容"
              name="Content"
              rules={[
                { required: true, message: '请输入内容' },
                { min: 10, message: '内容至少需要10个字符' }
              ]}
            >
              <TextArea
                rows={8}
                placeholder="请输入要处理的内容..."
                showCount
                maxLength={5000}
              />
            </Form.Item>

            <Form.Item
              label="行业类型"
              name="Industry"
              rules={[{ required: true, message: '请选择行业类型' }]}
            >
              <Select placeholder="选择行业类型">
                <Option value="企业服务">企业服务</Option>
                <Option value="消费文娱电商">消费文娱电商</Option>
                <Option value="金融">金融</Option>
                <Option value="半导体">半导体</Option>
                <Option value="信息科技">信息科技</Option>
                <Option value="材料">材料</Option>
                <Option value="能源">能源</Option>
                <Option value="制造">制造</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="输出类型"
              name="Output_type"
              rules={[{ required: true, message: '请选择输出类型' }]}
            >
              <Select 
                placeholder="选择输出类型"
                onChange={(value) => setOutputType(value)}
              >
                <Option value={TemplateType.DEEP}>深度纪要</Option>
                <Option value={TemplateType.DIALOGUE}>对话式纪要</Option>
                <Option value={TemplateType.KEY_POINTS}>关键点式纪要</Option>
                <Option value={TemplateType.TASK_ASSIGNMENT}>任务分配</Option>
                <Option value={TemplateType.CUSTOM}>自定义模板</Option>
              </Select>
            </Form.Item>

            {/* 条件显示的自定义模板字段 */}
            {outputType === TemplateType.CUSTOM && (
              <Form.Item
                label="自定义模板内容"
                name="User_Define_Template"
                rules={[
                  { required: true, message: '请输入自定义模板内容' },
                  { min: 10, message: '模板内容至少顰10个字符' }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="请输入自定义模板的内容和要求..."
                  showCount
                  maxLength={1000}
                />
              </Form.Item>
            )}
          </Form>
        </Card>

        {/* 响应结果 */}
        <Card 
          title="响应结果" 
          className="h-fit"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spin size="large" />
              <Text className="ml-3" type="secondary">
                正在处理请求...
              </Text>
            </div>
          ) : result ? (
            <div className="space-y-4">
              {/* 状态信息 */}
              {result.success !== undefined && (
                <div className="flex items-center gap-2">
                  <Text strong>状态:</Text>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {result.success ? '成功' : '失败'}
                  </span>
                </div>
              )}

              {/* 错误信息 */}
              {result.error && (
                <>
                  <Divider />
                  <div>
                    <Text strong className="text-red-600">错误信息:</Text>
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                      <Text className="text-red-800 font-mono text-sm">
                        {result.error}
                      </Text>
                    </div>
                  </div>
                </>
              )}

              {/* 响应数据 */}
              {result.data && (
                <>
                  <Divider />
                  <div>
                    <Text strong>响应数据:</Text>
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded overflow-auto max-h-96">
                      <pre className="text-sm font-mono whitespace-pre-wrap">
                        {formatJsonDisplay(result.data)}
                      </pre>
                    </div>
                  </div>
                </>
              )}

              
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Text type="secondary">
                提交表单后，响应结果将显示在这里
              </Text>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DebugPage;